/**
 * Quick Replies Component
 * Displays quick reply options for the user
 */

import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import type { QuickRepliesProps, QuickReply } from '@/types';
import { fadeInUp } from '@/utils/constants';
import { Heart, Phone, Recycle } from 'lucide-react';

function getQuickReplyIcon(reply: QuickReply) {
  if (reply.id === 'qr-1') {
    return <Heart className="h-4 w-4 text-nomade-accent" />;
  }
  if (reply.id === 'qr-2') {
    return <Phone className="h-4 w-4 text-nomade-accent" />;
  }
  if (reply.id === 'qr-3') {
    return <Recycle className="h-4 w-4 text-nomade-accent" />;
  }
  return <span aria-hidden className="text-sm">{reply.icon}</span>;
}

export function QuickReplies({ replies, onSelect }: QuickRepliesProps) {
  if (!replies || replies.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ staggerChildren: 0.1 }}
        className="max-w-3xl w-full px-4"
      >
        <div className="flex flex-col items-center gap-2 w-full">
          <div className="grid grid-cols-2 gap-2 w-full max-w-md mx-auto">
            {replies.slice(0, 2).map((reply, index) => (
              <motion.div
                key={reply.id}
                variants={fadeInUp}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ delay: index * 0.1 }}
                className="min-w-0"
              >
                <Button
                  variant="default"
                  onClick={() => onSelect(reply)}
                  className="w-full min-w-0 justify-center group relative overflow-hidden rounded-full px-3 py-2 text-xs sm:text-sm bg-black/60 hover:bg-nomade-accent text-white font-medium backdrop-blur-md border border-white/20 hover:border-nomade-accent shadow-lg"
                >
                  <span className="flex items-center gap-1 text-white min-w-0">
                    {getQuickReplyIcon(reply)}
                    <span className="truncate">{reply.text}</span>
                  </span>

                  {/* Hover effect - pointer-events-none so click reaches button */}
                  <motion.div
                    className="absolute inset-0 bg-white/10 pointer-events-none"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </Button>
              </motion.div>
            ))}
          </div>

          {replies[2] && (
            <motion.div
              key={replies[2].id}
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ delay: 0.2 }}
            >
              <Button
                variant="default"
                onClick={() => onSelect(replies[2])}
                className="group relative overflow-hidden rounded-full px-4 py-2 text-xs sm:text-sm bg-black/60 hover:bg-nomade-accent text-white font-medium backdrop-blur-md border border-white/20 hover:border-nomade-accent shadow-lg"
              >
                <span className="flex items-center gap-2 text-white">
                  {getQuickReplyIcon(replies[2])}
                  <span>{replies[2].text}</span>
                </span>

                {/* Hover effect - pointer-events-none so click reaches button */}
                <motion.div
                  className="absolute inset-0 bg-white/10 pointer-events-none"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </Button>
            </motion.div>
          )}
        </div>

        {/* Helper text - Masqué sur mobile car déjà en bas */}
        <motion.p
          variants={fadeInUp}
          className="hidden md:block text-center text-white/60 text-sm mt-4"
        >
          Sélectionnez une option ou continuez de parler
        </motion.p>
      </motion.div>
    </AnimatePresence>
  );
}
